<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from 'reka-ui'
import { CheckIcon, ChevronDownIcon, SearchIcon } from '@lucide/vue'
import { cn } from '@/lib/utils'

export interface SearchableSelectOption {
  value: string
  label: string
}

const props = defineProps<{
  options: SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  id?: string
  invalid?: boolean
  class?: string
}>()

/** `''` means nothing is selected. */
const model = defineModel<string>({ default: '' })

const open = ref(false)
const searchTerm = ref('')

const selected = computed(() => props.options.find((option) => option.value === model.value))

/** Case- and accent-insensitive, so "cote" finds "Côte d’Ivoire". */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

// Matches the label anywhere, or the code (e.g. "NL") from its start.
const filteredOptions = computed(() => {
  const term = normalize(searchTerm.value.trim())

  if (term === '') return props.options

  return props.options.filter(
    (option) => normalize(option.label).includes(term) || normalize(option.value).startsWith(term),
  )
})

function handleOpenChange(value: boolean): void {
  open.value = value
  if (!value) searchTerm.value = ''
}
</script>

<template>
  <ComboboxRoot v-model="model" :open="open" ignore-filter @update:open="handleOpenChange">
    <ComboboxAnchor as-child>
      <ComboboxTrigger
        :id="id"
        :aria-invalid="invalid || undefined"
        :class="
          cn(
            'border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 dark:bg-input/30 dark:hover:bg-input/50 flex h-9 w-full items-center justify-between gap-1.5 rounded-md border bg-transparent py-2 pr-2 pl-2.5 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-3 aria-invalid:ring-3 disabled:cursor-not-allowed disabled:opacity-50',
            props.class,
          )
        "
      >
        <span class="line-clamp-1 text-left" :class="selected ? '' : 'text-muted-foreground'">
          {{ selected?.label ?? placeholder }}
        </span>
        <ChevronDownIcon class="text-muted-foreground pointer-events-none size-4 shrink-0" />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxPortal>
      <ComboboxContent
        position="popper"
        :side-offset="4"
        class="bg-popover text-popover-foreground ring-foreground/10 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 z-50 w-(--reka-combobox-trigger-width) min-w-36 overflow-hidden rounded-md shadow-md ring-1"
      >
        <div class="flex items-center gap-2 border-b px-2.5">
          <SearchIcon class="text-muted-foreground size-4 shrink-0" />
          <!-- Without display-value reka fills the search box with the selected option's raw value. -->
          <ComboboxInput
            v-model="searchTerm"
            :display-value="() => ''"
            auto-focus
            :placeholder="searchPlaceholder"
            class="placeholder:text-muted-foreground h-9 w-full bg-transparent text-sm outline-none"
          />
        </div>
        <ComboboxViewport class="max-h-64 overflow-y-auto p-1">
          <ComboboxEmpty class="text-muted-foreground py-6 text-center text-sm">
            {{ emptyText }}
          </ComboboxEmpty>
          <ComboboxItem
            v-for="option in filteredOptions"
            :key="option.value"
            :value="option.value"
            :text-value="option.label"
            class="data-highlighted:bg-accent data-highlighted:text-accent-foreground relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none"
          >
            {{ option.label }}
            <ComboboxItemIndicator class="absolute right-2 flex size-4 items-center justify-center">
              <CheckIcon class="size-4" />
            </ComboboxItemIndicator>
          </ComboboxItem>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>
